import { z } from 'zod';
import { getStroke } from 'perfect-freehand';

export const CANVAS_WIDTH = 1000;
export const MAX_CANVAS_HEIGHT = 12000;
export const MAX_CANVAS_BYTES = 200000;
export const workingContentSchema = z
  .object({
    format_version: z.literal(1),
    height: z.number().int().min(600).max(MAX_CANVAS_HEIGHT),
    background: z.enum(['blank', 'grid', 'dots']),
    text: z.string().max(20000),
    question: z.string().max(5000).optional(),
    strokes: z
      .array(
        z
          .object({
            id: z.string().uuid(),
            colour: z.enum(['ink', 'sky', 'coral']),
            width: z.number().finite().min(1).max(12),
            pointer: z.enum(['pen', 'touch', 'mouse']),
            points: z
              .array(
                z
                  .object({
                    x: z.number().finite().min(0).max(CANVAS_WIDTH),
                    y: z.number().finite().min(0).max(MAX_CANVAS_HEIGHT),
                    pressure: z.number().finite().min(0).max(1),
                    time: z.number().finite().nonnegative(),
                  })
                  .strict(),
              )
              .min(1)
              .max(2000),
          })
          .strict(),
      )
      .max(1000),
  })
  .strict()
  .refine((content) =>
    content.strokes.every((stroke) => stroke.points.every((p) => p.y <= content.height)),
  );
export type WorkingContent = z.infer<typeof workingContentSchema>;
export type InkStroke = WorkingContent['strokes'][number];
export type InkPoint = InkStroke['points'][number];
export interface WorkingContext {
  kind: 'practice' | 'study' | 'mock' | 'free' | 'material';
  question_id: string;
  session_id?: string;
}
export interface WorkingRow {
  id: string;
  version: number;
  locked: boolean;
  content: WorkingContent;
  context: WorkingContext;
}
export const blankWorking = (): WorkingContent => ({
  format_version: 1,
  height: 800,
  background: 'grid',
  text: '',
  strokes: [],
});
export function inkPath(stroke: InkStroke): string {
  const outline = getStroke(
    stroke.points.map((p) => [p.x, p.y, p.pressure]),
    {
      size: stroke.width,
      thinning: 0.4,
      simulatePressure: stroke.pointer !== 'pen',
      smoothing: 0.5,
    },
  );
  return outline.length ? `M ${outline.map((p) => p.join(' ')).join(' L ')} Z` : '';
}
export function containsPoint(
  polygon: Array<{ x: number; y: number }>,
  point: { x: number; y: number },
): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    if (
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
    )
      inside = !inside;
  }
  return inside;
}
export function strokeNear(
  stroke: InkStroke,
  point: { x: number; y: number },
  radius = 14,
): boolean {
  return stroke.points.some((p, index) => {
    const previous = stroke.points[index - 1] ?? p;
    const dx = p.x - previous.x;
    const dy = p.y - previous.y;
    const length = dx * dx + dy * dy;
    const t = length
      ? Math.min(
          1,
          Math.max(0, ((point.x - previous.x) * dx + (point.y - previous.y) * dy) / length),
        )
      : 0;
    return (
      Math.hypot(point.x - previous.x - t * dx, point.y - previous.y - t * dy) <=
      radius + stroke.width / 2
    );
  });
}
export function moveStrokes(
  content: WorkingContent,
  selected: string[],
  dx: number,
  dy: number,
): WorkingContent {
  const points = content.strokes
    .filter((stroke) => selected.includes(stroke.id))
    .flatMap((stroke) => stroke.points);
  if (!points.length) return content;
  const x = Math.max(
    -Math.min(...points.map((p) => p.x)),
    Math.min(dx, CANVAS_WIDTH - Math.max(...points.map((p) => p.x))),
  );
  const y = Math.max(
    -Math.min(...points.map((p) => p.y)),
    Math.min(dy, MAX_CANVAS_HEIGHT - Math.max(...points.map((p) => p.y))),
  );
  const height = Math.min(
    MAX_CANVAS_HEIGHT,
    Math.max(content.height, Math.ceil(Math.max(...points.map((p) => p.y)) + y + 100)),
  );
  return {
    ...content,
    height,
    strokes: content.strokes.map((stroke) =>
      selected.includes(stroke.id)
        ? { ...stroke, points: stroke.points.map((p) => ({ ...p, x: p.x + x, y: p.y + y })) }
        : stroke,
    ),
  };
}

export const isMathematicsSubject = (subject: string) =>
  /mathematics|numeracy|maths/i.test(subject);
